import AuthorizationError, {
  isAuthorizationProblem,
} from "@/error/api/authorization";
import FailedValidation, {
  isFailedValidationProblem,
} from "@/error/api/failed-validation";
import { AxiosError, AxiosResponse } from "axios";
import { Vue } from "vue-class-component";

export type ResponseInterceptor = (res: AxiosResponse) => AxiosResponse;
export type ErrorResponseInterceptor = (err: AxiosError) => unknown;
export type VmResponseInterceptor = (
  vm: Vue,
  ...args: Parameters<ResponseInterceptor>
) => ReturnType<ResponseInterceptor>;
export type VmErrorResponseInterceptor = (
  vm: Vue,
  ...args: Parameters<ErrorResponseInterceptor>
) => ReturnType<ErrorResponseInterceptor>;
/**
 * Sets isAuthenticated to false in store if response returns 401.
 * @param res - The response
 * @param vm - The root vue instance
 */

/**
 * Creates a new Response interceptor function with the ViewModel instance
 * applied.
 *
 * @remarks This function is needed because at the time of creation of the
 * httpClient the root ViewModel has not being instanciated yet.
 */
export function bindVmToResponseInterceptor(
  fn: VmResponseInterceptor,
  vm: Vue
): ResponseInterceptor {
  return (res: AxiosResponse) => fn(vm, res);
}

/**
 * Creates a new ErrorResponce interceptor function with the ViewModel instance
 * applied.
 *
 * @remarks This function is needed because at the time of creation of the
 * httpClient the root ViewModel has not being instanciated yet.
 */
export function bindVmToErrorResponseInterceptor(
  fn: VmErrorResponseInterceptor,
  vm: Vue
): ErrorResponseInterceptor {
  return (err: AxiosError) => fn(vm, err);
}

export function errorResponseInterceptor(vm: Vue, err: AxiosError): void {
  console.error(err);
  const axiosErr = err as AxiosError;
  // Failed Validation
  if (axiosErr.response && isFailedValidationProblem(axiosErr.response.data)) {
    throw new FailedValidation(axiosErr.response.data);
  }
  if (axiosErr.response && isAuthorizationProblem(axiosErr.response.data)) {
    throw new AuthorizationError(axiosErr.response.data);
  }
  // Unauthenticated
  if (axiosErr.response && axiosErr.response.status === 401) {
    vm.$store.commit("authUser/setIsAuthenticated", false);
  }
  throw err;
}
